import React, { ComponentClass, CSSProperties, FunctionComponent, ReactNode } from 'react';
import { BorderPanel } from '../../layout/borderpanel';
import { VerticalPanel } from '../../layout/verticalpanel';
import { Headline } from '../headline';
import { PrimaryText } from '../primarytext';
import { SecondaryText } from '../secondarytext';
import { TertiaryText } from '../tertiarytext';
import { VerticalSpacer } from '../../layout/verticalspacer';
import { FlexPanel } from '../../layout/flexpanel';
import { Panel } from '../../layout/panel';
import classNames from 'classnames';
import { EllipsisConfig } from '../text';
import {TooltipProps} from '../tooltip';

/**
 * {@docCategory Details}
 */
export interface LineConfig {
   /**
    * Strikethrough style
    */
   strikethrough?: boolean;

   /**
    * Underline style
    */
   underline?: boolean;

   /**
    * Display ellipsis when text overflows
    */
   ellipsis?: boolean | EllipsisConfig;

   /**
    * Sets the text
    */
   text?: string;

   /**
    * Sets a className
    */
   className?: string;

   /**
    * Sets styles
    */
   style?: CSSProperties;
}

export declare type LineConfigType =
   string
   | LineConfig
   | undefined
   | Array<string | LineConfig | undefined>;

/**
 * {@docCategory Details}
 */
export interface DetailsProps {
   /**
    * Determines if the panel has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * The prefix content.
    */
   prefix?: ReactNode;
   prefixAlignment?: 'top' | 'center' | 'bottom';

   /**
    * Sets a className for the prefix container.
    */
   prefixClassName?: string;

   /**
    * The suffix content.
    */
   suffix?: ReactNode;
   suffixAlignment?: 'top' | 'center' | 'bottom';

   /**
    * Sets a className for the suffix container.
    */
   suffixClassName?: string;

   /**
    * The headlines.
    */
   headlines?: LineConfigType;

   /**
    * The primary lines.
    */
   primaryLines?: LineConfigType;

   /**
    * The secondary lines.
    */
   secondaryLines?: LineConfigType;

   /**
    * The tertiary lines.
    */
   tertiaryLines?: LineConfigType;

   /**
    * The vertical alignment of the lines and content
    * @defaultvalue top
    */
   verticalAlignment?: 'top' | 'center' | 'bottom';

   /**
    * The content.
    */
   children?: ReactNode;

   /**
    * Sets a className
    */
   className?: string;

   /**
    * Sets styles
    */
   style?: CSSProperties;

   /**
    * Called when the user clicks the details.
    */
   onClick?: () => void;
}

const prefixContainer = (props: DetailsProps) => {
   if (props.prefix) {
      return (
         <VerticalPanel
            className={`uf-details-prefix-container ${props.prefixClassName || ''}`}
            align={props.prefixAlignment}
         >
            {props.prefix}
         </VerticalPanel>
      );
   }

   return undefined;
};

const suffixContainer = (props: DetailsProps) => {
   if (props.suffix) {
      return (
         <VerticalPanel
            className={`uf-details-suffix-container ${props.suffixClassName || ''}`}
            align={props.suffixAlignment}
         >
            {props.suffix}
         </VerticalPanel>
      );
   }

   return undefined;
};

declare type LineConfigProps = LineConfig;

const lines = (lines: LineConfigType,
               elementType: FunctionComponent<LineConfigProps> | ComponentClass<LineConfigProps> | string,
               uniqueNameForLines: string) => {
   if (Array.isArray(lines)) {
      return lines.map((line, index) => {
         const key = uniqueNameForLines+'_'+index;
         if (typeof line === 'string') {
            return React.createElement(elementType, {
               style: { flexShrink: 0, maxWidth: '100%' },
               key: key
            }, line);
         }

         if (typeof line === 'object') {
            const { text, style: linePropsStyle, ...lineProps } = line as LineConfig;

            const style = {
               flexShrink: 0,
               maxWidth: '100%', // Without this, the ellipsis will not work in a flexbox
              ...linePropsStyle
            };

            return React.createElement(elementType, {
               ...lineProps,
               style: style,
               key: key
            }, text);
         }

         if (line === undefined) {
            return (<VerticalSpacer key={key} size={'small'}/>);
         }
      });
   }

   if (typeof lines === 'string') {
      return React.createElement(elementType, {
         style: { flexShrink: 0, maxWidth: '100%' }
      }, lines);
   }

   if (typeof lines === 'object') {
      const { text, style: linePropsStyle, ...lineProps } = lines as LineConfig;

      const style = {
         flexShrink: 0,
         maxWidth: '100%', // Without this, the ellipsis will not work in a flexbox
         ...linePropsStyle
      };

      return React.createElement(elementType, {
         ...lineProps,
         style: style
      }, text);
   }

   return undefined;
};


const headlines = (props: DetailsProps) => {
   return lines(props.headlines, Headline, 'HeadlineLines');
};

const primaryLines = (props: DetailsProps) => {
   return lines(props.primaryLines, PrimaryText, 'PrimaryTextLines');
};

const secondaryLines = (props: DetailsProps) => {
   return lines(props.secondaryLines, SecondaryText, 'SecondaryTextLines');
};

const tertiaryLines = (props: DetailsProps) => {
   return lines(props.tertiaryLines, TertiaryText, 'TertiaryTextLines');
};

export const Details = (props: DetailsProps) => {
   const classes = classNames({
                                 'uf-details-content-container': true,
                                 'uf-details-content-container-no-prefix': props.prefix === undefined,
                                 'uf-details-content-container-with-prefix': props.prefix !== undefined,
                                 'uf-details-content-container-no-suffix': props.suffix === undefined,
                                 'uf-details-content-container-with-suffix': props.suffix !== undefined,
                              });

   const verticalAlignment = props.verticalAlignment || 'top';

   return (
      <Panel
         border={props.border}
         horizontalFill={true}
         className={'uf-details'}
         onClick={props.onClick}
      >
         <BorderPanel
            left={prefixContainer(props)}
            right={suffixContainer(props)}
            style={props.style}
            className={props.className}
         >
            <VerticalPanel padding={false} verticalFill={true} align={verticalAlignment}>
               <VerticalPanel className={classes} verticalFill={false}>
                  {headlines(props)}
                  {primaryLines(props)}
                  {secondaryLines(props)}
                  {tertiaryLines(props)}
                  {props.children}
               </VerticalPanel>
            </VerticalPanel>
         </BorderPanel>
      </Panel>
   );
};
